/**
 * @file Header.tsx
 * @description Top header bar with view title and real-time clock.
 */


import { RefreshCw, Volume2, VolumeX, Globe } from "lucide-react";
import { AppView } from "../../types";
import { useLanguage, LANGUAGE_OPTIONS, LanguageCode } from "../../contexts/LanguageContext";
import { useSpeech } from "../../hooks/useSpeech";

interface HeaderProps {
  activeView: AppView;
  onRefresh?: () => void;
}

const VIEW_LABELS: Record<AppView, { titleKey: string; subKey: string }> = {
  overview: { titleKey: "header_overview_title", subKey: "header_overview_sub" },
  "data-health": { titleKey: "header_health_title", subKey: "header_health_sub" },
  ingest: { titleKey: "header_ingest_title", subKey: "header_ingest_sub" },
  users: { titleKey: "header_users_title", subKey: "header_users_sub" },
  clinical: { titleKey: "header_clinical_title", subKey: "header_clinical_sub" },
  "doctor-dash": { titleKey: "header_doctor_title", subKey: "header_doctor_sub" },
  "patient-intel": { titleKey: "header_doctor_title", subKey: "header_doctor_sub" },
  "nurse-dash": { titleKey: "header_nurse_title", subKey: "header_nurse_sub" },
  "lab-dash": { titleKey: "header_lab_title", subKey: "header_lab_sub" },
  "desk-dash": { titleKey: "header_desk_title", subKey: "header_desk_sub" },
  "dept-dash": { titleKey: "header_dept_title", subKey: "header_dept_sub" },
  "mgmt-dash": { titleKey: "header_mgmt_title", subKey: "header_mgmt_sub" },
  "billing": { titleKey: "nav_billing", subKey: "" },
  "payment-dash": { titleKey: "Payment & Settlement", subKey: "" },
  "alerts-dash": { titleKey: "Emergency & Alerts", subKey: "" },
};

export function Header({ activeView, onRefresh }: HeaderProps) {
  const { language, setLanguage, t } = useLanguage();
  const { speak, stop, isSpeaking, supported } = useSpeech();

  const { titleKey, subKey } = VIEW_LABELS[activeView];

  const handleReadUpdates = () => {
    if (isSpeaking) {
      stop();
    } else {
      speak("Hospital operations update. You have active conflicts requiring review.");
    }
  };

  return (
    <header
      className="fixed top-0 left-0 md:left-64 right-0 h-16 z-30 flex items-center justify-between px-4 md:px-6 transition-all duration-300"
      style={{
        background: "rgba(4, 13, 26, 0.8)",
        backdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <div className="animate-fade-in truncate mr-4" key={activeView}>
        <h1 className="text-white font-semibold text-lg leading-tight truncate">{t(titleKey)}</h1>
        <p className="text-gray-500 text-xs truncate hidden sm:block">{t(subKey)}</p>
      </div>

      <div className="flex items-center gap-3 md:gap-4 flex-shrink-0">
        
        {/* Language Selector */}
        <div className="relative group flex items-center">
          <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-gray-300 text-xs hover:bg-white/10 transition-colors cursor-pointer">
            <Globe className="w-3.5 h-3.5" />
            <span className="hidden sm:inline font-medium uppercase tracking-wider">{language}</span>
          </div>
          <div className="absolute right-0 top-full mt-1 w-40 py-2 rounded-xl bg-[#040d1a] border border-white/10 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
            {LANGUAGE_OPTIONS.map((opt) => (
              <button
                key={opt.code}
                onClick={() => setLanguage(opt.code as LanguageCode)}
                className={`w-full text-left px-4 py-2 text-xs hover:bg-white/5 transition-colors ${language === opt.code ? "text-teal-400 font-medium bg-teal-500/5" : "text-gray-300"}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Read Updates TTS Button */}
        {supported && (
          <button
            onClick={handleReadUpdates}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
              isSpeaking 
                ? "bg-amber-500/20 text-amber-400 border border-amber-500/30 animate-pulse-slow" 
                : "bg-teal-500/10 text-teal-400 border border-teal-500/20 hover:bg-teal-500/20"
            }`}
          >
            {isSpeaking ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">{isSpeaking ? t("btn_stop") : t("btn_read_updates")}</span>
          </button>
        )}

        {/* Live indicator */}
        <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-500/10 border border-teal-500/20">
          <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse-slow" />
          <span className="text-teal-400 text-[10px] font-bold tracking-wider">LIVE</span>
        </div>

        {onRefresh && (
          <button
            id="header-refresh-btn"
            onClick={onRefresh}
            className="p-2 rounded-xl hover:bg-white/5 text-gray-400 hover:text-white transition-all"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        )}
      </div>
    </header>
  );
}
